import { useEffect, useState, useRef } from 'react';
import { Palette, Upload, X, Loader2 } from 'lucide-react';
import { Input } from '@/core/ui/components';
import { useFileUpload } from '@/shared/hooks';
import type { WizardFormData } from '../types';

interface BrandingStepProps {
  formData: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
  onValidChange: (isValid: boolean) => void;
  errors: Record<string, string>;
}

const DEFAULT_COLORS = [
  { label: 'Indigo', primary: '#4F46E5', secondary: '#818CF8' },
  { label: 'Blue', primary: '#2563EB', secondary: '#60A5FA' },
  { label: 'Green', primary: '#16A34A', secondary: '#4ADE80' },
  { label: 'Purple', primary: '#9333EA', secondary: '#C084FC' },
  { label: 'Orange', primary: '#EA580C', secondary: '#FB923C' },
  { label: 'Red', primary: '#DC2626', secondary: '#F87171' },
];

export function BrandingStep({ formData, onChange, onValidChange }: BrandingStepProps) {
  const [logoPreview, setLogoPreview] = useState<string | null>(formData.branding.logoUrl || null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { upload, isUploading } = useFileUpload('general', {
    allowedTypes: ['image/*'],
    maxSizeMB: 5,
    onSuccess: (file) => {
      setLogoPreview(file.url);
      onChange({
        branding: {
          ...formData.branding,
          logoUrl: file.url,
          logoFileId: file.id,
        },
      });
    },
  });

  useEffect(() => {
    // Branding is optional, always valid
    onValidChange(true);
  }, [onValidChange]);

  const handleBrandingChange = (field: keyof typeof formData.branding, value: string) => {
    onChange({
      branding: {
        ...formData.branding,
        [field]: value,
      },
    });
  };

  const handleFileSelect = async (file: File) => {
    await upload(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    onChange({
      branding: {
        ...formData.branding,
        logoUrl: '',
        logoFileId: '',
      },
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleColorPresetClick = (primary: string, secondary: string) => {
    onChange({
      branding: {
        ...formData.branding,
        primaryColor: primary,
        secondaryColor: secondary,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
          <Palette className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-theme">Branding</h3>
          <p className="text-sm text-theme-secondary">Customize the tenant's look and feel (optional)</p>
        </div>
      </div>

      {/* Logo Upload */}
      <div>
        <label className="block text-sm font-medium text-theme mb-2">Logo</label>
        {logoPreview ? (
          <div className="flex items-start gap-4">
            <div className="relative">
              <div className="w-24 h-24 rounded-lg border border-theme overflow-hidden bg-white dark:bg-slate-800 flex items-center justify-center">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="max-w-full max-h-full object-contain"
                  onError={() => setLogoPreview(null)}
                />
              </div>
              <button
                type="button"
                onClick={handleRemoveLogo}
                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1">
              <p className="text-sm text-theme-secondary">Logo uploaded successfully</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
              >
                Change logo
              </button>
            </div>
          </div>
        ) : (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`
              border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
              ${isDragOver
                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                : 'border-slate-300 dark:border-slate-600 hover:border-indigo-400 dark:hover:border-indigo-500'
              }
              ${isUploading ? 'pointer-events-none opacity-60' : ''}
            `}
          >
            {isUploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-2" />
                <p className="text-sm text-theme-secondary">Uploading...</p>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-theme-secondary">
                  Drag and drop your logo here, or <span className="text-indigo-600 dark:text-indigo-400">browse</span>
                </p>
                <p className="text-xs text-theme-tertiary mt-1">PNG, JPG, SVG up to 5MB</p>
              </>
            )}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
          className="hidden"
        />
      </div>

      {/* Color Presets */}
      <div>
        <label className="block text-sm font-medium text-theme mb-3">Color Preset</label>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {DEFAULT_COLORS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => handleColorPresetClick(preset.primary, preset.secondary)}
              className={`p-3 rounded-lg border-2 transition-all ${
                formData.branding.primaryColor === preset.primary
                  ? 'border-indigo-600'
                  : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'
              }`}
            >
              <div className="flex gap-1 justify-center mb-2">
                <div
                  className="w-6 h-6 rounded-full"
                  style={{ backgroundColor: preset.primary }}
                />
                <div
                  className="w-6 h-6 rounded-full"
                  style={{ backgroundColor: preset.secondary }}
                />
              </div>
              <span className="text-xs text-theme-secondary">{preset.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Colors */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-theme mb-2">Primary Color</label>
          <div className="flex gap-2">
            <input
              type="color"
              value={formData.branding.primaryColor || '#4F46E5'}
              onChange={(e) => handleBrandingChange('primaryColor', e.target.value)}
              className="w-12 h-10 rounded border border-theme cursor-pointer"
            />
            <Input
              value={formData.branding.primaryColor || '#4F46E5'}
              onChange={(e) => handleBrandingChange('primaryColor', e.target.value)}
              placeholder="#4F46E5"
              className="flex-1"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-theme mb-2">Secondary Color</label>
          <div className="flex gap-2">
            <input
              type="color"
              value={formData.branding.secondaryColor || '#818CF8'}
              onChange={(e) => handleBrandingChange('secondaryColor', e.target.value)}
              className="w-12 h-10 rounded border border-theme cursor-pointer"
            />
            <Input
              value={formData.branding.secondaryColor || '#818CF8'}
              onChange={(e) => handleBrandingChange('secondaryColor', e.target.value)}
              placeholder="#818CF8"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      {/* Preview */}
      <div>
        <label className="block text-sm font-medium text-theme mb-3">Preview</label>
        <div className="p-4 rounded-lg border border-theme bg-white dark:bg-slate-900">
          <div className="flex items-center gap-3">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="w-10 h-10 object-contain" />
            ) : (
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: formData.branding.primaryColor || '#4F46E5' }}
              >
                {formData.name.charAt(0) || 'T'}
              </div>
            )}
            <span className="font-semibold text-theme">{formData.name || 'Tenant Name'}</span>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="px-4 py-2 rounded-lg text-white text-sm font-medium"
              style={{ backgroundColor: formData.branding.primaryColor || '#4F46E5' }}
            >
              Primary Button
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-lg text-white text-sm font-medium"
              style={{ backgroundColor: formData.branding.secondaryColor || '#818CF8' }}
            >
              Secondary Button
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
